import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import ArchiveIcon from "@mui/icons-material/Archive";
import { ChatContext } from "../context/chatcontext";

export default function Sidebar() {
  const {
    currentChat,
    startNewChat,
    switchChat,
    deleteChat,
    archiveChat,
    clearAllChats,
    createProject,
    projects,
    getChatsByProject,
    currentProject,
    setCurrentProject,
  } = useContext(ChatContext);

  const [selectedProject, setSelectedProject] = useState(currentProject);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Menu states
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [activeMenuChat, setActiveMenuChat] = useState(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);

  useEffect(() => setSelectedProject(currentProject), [currentProject]);

  const handleChatMenuOpen = (event, chat) => {
    event.stopPropagation();
    setChatMenuAnchor(event.currentTarget);
    setActiveMenuChat(chat);
  };

  const handleChatMenuClose = () => {
    setChatMenuAnchor(null);
    setActiveMenuChat(null);
  };

  const handleHeaderMenuOpen = (event) => {
    setHeaderMenuAnchor(event.currentTarget);
  };

  const handleHeaderMenuClose = () => {
    setHeaderMenuAnchor(null);
  };

  const handleNewChat = () => startNewChat(selectedProject);
  const handleCreateProject = () => {
    const name = prompt("Enter new project name:");
    if (name) {
      const proj = createProject({ name });
      setSelectedProject(proj.id);
      alert(`✅ Project "${name}" created!`);
    }
  };

  const projectChats = getChatsByProject(selectedProject);
  const filteredChats = projectChats.filter(chat => 
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box
      sx={{
        width: 260,
        height: "100%",
        bgcolor: "#FFFFFF",
        background: "linear-gradient(180deg, #FFFFFF, #F0F8FF, #E6F3FF)",
        color: "black",
        display: "flex",
        flexDirection: "column",
        p: 2,
        borderRight: "1px solid #87CEEB",
        boxShadow: "4px 0 10px rgba(135, 206, 235, 0.2)",
      }}
    >
      {/* 🟣 New Chat */}
      <Button
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleNewChat}
        sx={{
          mb: 2,
          background: "linear-gradient(90deg, #87CEEB, #00BFFF)",
          "&:hover": { background: "linear-gradient(90deg, #00BFFF, #87CEEB)" },
          textTransform: "none",
          fontWeight: 600,
          color: "#FFFFFF",
        }}
      >
        New Chat
      </Button>

      {/* 📁 Create Project */}
      <Button
        variant="outlined"
        startIcon={<FolderOpenIcon />}
        onClick={handleCreateProject}
        sx={{
          mb: 2,
          color: "#87CEEB",
          borderColor: "#87CEEB",
          "&:hover": {
            borderColor: "#00BFFF",
            backgroundColor: "rgba(135, 206, 235, 0.1)",
          },
          textTransform: "none",
        }}
      >
        Create Project
      </Button>

      {/* 🔽 Project Filter */}
      <select
        value={selectedProject || ""}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : null;
          setSelectedProject(val);
          setCurrentProject(val);
        }}
        style={{
          background: "#FFFFFF",
          color: "#87CEEB",
          border: "1px solid #87CEEB",
          borderRadius: "6px",
          padding: "8px",
          marginBottom: "15px",
          outline: "none",
        }}
      >
        <option value="">All Projects</option>
        {projects.map((proj) => (
          <option key={proj.id} value={proj.id}>
            {proj.name}
          </option>
        ))}
      </select>

      <Divider sx={{ borderColor: "#87CEEB", mb: 2 }} />

      {/* 🔍 Search Bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search chats..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            color: "black",
            fontSize: "0.85rem",
            bgcolor: "rgba(0, 0, 0, 0.05)",
            "& fieldset": { borderColor: "rgba(135, 206, 235, 0.3)" },
            "&:hover fieldset": { borderColor: "#87CEEB" },
            "&.Mui-focused fieldset": { borderColor: "#87CEEB" },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#87CEEB", fontSize: "1.1rem" }} />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "#87CEEB",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontSize: "0.75rem",
            fontWeight: 700,
          }}
        >
          Chat History
        </Typography>
        <IconButton 
          size="small" 
          onClick={handleHeaderMenuOpen}
          sx={{ color: "#666666", "&:hover": { color: "#87CEEB" } }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={headerMenuAnchor}
          open={Boolean(headerMenuAnchor)}
          onClose={handleHeaderMenuClose}
          PaperProps={{
            sx: {
              bgcolor: "#FFFFFF",
              color: "black",
              border: "1px solid #87CEEB",
            }
          }}
        >
          <MenuItem 
            onClick={() => {
              handleHeaderMenuClose();
              if (window.confirm("Clear all chat history? This cannot be undone.")) {
                clearAllChats();
              }
            }}
            sx={{ "&:hover": { bgcolor: "rgba(255, 68, 68, 0.1)", color: "#ff4444" } }}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
            <ListItemText primary="Clear All History" primaryTypographyProps={{ fontSize: '0.85rem' }} />
          </MenuItem>
        </Menu>
      </Box>

      {/* 💬 Chats */}
      <List
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            background: "#87CEEB",
            borderRadius: "3px",
          },
        }}
      >
        {filteredChats.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", textAlign: "center", mt: 3 }}
          >
            {searchQuery ? "No matching chats" : "No chats yet. Start one!"}
          </Typography>
        ) : (
          filteredChats.map((chat) => {
            const project = projects.find((p) => p.id === chat.projectId);
            const projectLabel = project
              ? `Project: ${project.name}`
              : "Project: None";

            return (
              <ListItem
                key={chat.id}
                disablePadding
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={(e) => handleChatMenuOpen(e, chat)}
                    sx={{
                      color: "#6b7280",
                      "&:hover": { color: "#87CEEB" },
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  mb: 0.5,
                  borderRadius: "6px",
                  bgcolor:
                    chat.id === currentChat
                      ? "rgba(135, 206, 235, 0.15)"
                      : "transparent",
                  "&:hover": {
                    bgcolor: chat.id === currentChat 
                      ? "rgba(135, 206, 235, 0.2)" 
                      : "rgba(0, 0, 0, 0.05)",
                  },
                  "& .MuiListItemSecondaryAction-root": {
                    display: "block",
                  }
                }}
              >
                <ListItemButton 
                  onClick={() => switchChat(chat.id)}
                  sx={{ 
                    pr: 5, // Room for 3-dots
                    borderRadius: "6px",
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          color:
                            chat.id === currentChat ? "#00BFFF" : "#333333",
                          fontSize: "0.9rem",
                          fontWeight:
                            chat.id === currentChat ? "bold" : "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%", // Explicitly take full width
                          display: "block",
                        }}
                      >
                        {chat.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          color: "#666666",
                          fontSize: "0.75rem",
                          mt: 0.5,
                        }}
                        noWrap
                      >
                        {projectLabel}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>

      {/* Chat Options Menu */}
      <Menu
        anchorEl={chatMenuAnchor}
        open={Boolean(chatMenuAnchor)}
        onClose={handleChatMenuClose}
        PaperProps={{
          sx: {
            bgcolor: "#FFFFFF",
            color: "black",
            border: "1px solid #87CEEB",
            minWidth: 150,
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            if (activeMenuChat) archiveChat(activeMenuChat.id);
            handleChatMenuClose();
          }}
          sx={{ fontSize: '0.85rem' }}
        >
          <ListItemIcon><ArchiveIcon fontSize="small" sx={{ color: '#666666' }} /></ListItemIcon>
          Archive
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (activeMenuChat && window.confirm("Delete this chat?")) {
              deleteChat(activeMenuChat.id);
            }
            handleChatMenuClose();
          }}
          sx={{ 
            fontSize: '0.85rem',
            "&:hover": { bgcolor: "rgba(255, 68, 68, 0.1)", color: "#ff4444" }
          }}
        >
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <Divider sx={{ borderColor: "#87CEEB", mt: 1, mb: 1 }} />
      <Typography
        variant="caption"
        sx={{
          textAlign: "center",
          color: "#87CEEB",
          mt: 1,
          fontSize: "0.7rem",
        }}
      >
        v1.0 © TSG AI
      </Typography>
    </Box>
  );
}
